import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IconFromSet,
  IconPicker,
  IconValue
} from '@gettactic/components';
import { TacticIconsDictionary } from '@gettactic/components/src/icons/tactic-icons/dictionary';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@gettactic/components/src/primitives/Popover';
import { useDisclosure } from '@gettactic/helpers';
import {
  Armchair,
  ArrowDown01,
  AudioWaveform,
  Boxes,
  ChevronUp,
  Circle,
  Eclipse,
  Hand,
  MousePointer,
  Square,
  Type
} from 'lucide-react';
import React from 'react';
import { Furniture } from '../../../../furniture-list';
import { FurnitureSelectionModal } from '../../../FurnitureSelectionModal';
import { TooltipProvider } from '../../../ui/tooltip';
import { useMapEditor } from '../../contexts/MapEditorContext';
import { MapEditorTool } from '../../types';
import { EditorTooltip } from '../controls/EditorTooltip';
import { useEditorShortcuts } from '../shortcuts/EditorShortcutContext/useEditorShortcuts';
import { ToolbarButton } from './ToolbarButton';
import { DrawShape } from '../../../../types';

interface BottomToolbarProps {
  activeTool: MapEditorTool;
  onToolChange: (tool: MapEditorTool) => void;
}

const classes = {
  ToolBar:
    'fixed bottom-10 left-1/2 z-[9999] shadow-xl bg-background-primary rounded-lg overflow-hidden -translate-x-1/2 flex',
  PopoverContent: 'bg-white p-0 w-[550px]',
  PopoverArrow: 'fill-muted'
};

export const ToolsPickerToolbar: React.FC<BottomToolbarProps> = ({
  activeTool,
  onToolChange
}) => {
  const {
    iconToolSelectedIcon,
    setIconToolSelectedIcon,
    furnitureToolSelectedFurniture,
    setFurnitureToolSelectedFurniture,
    drawTool,
    setDrawTool
  } = useMapEditor();
  const { shortcuts } = useEditorShortcuts();
  const iconPickerDisclosure = useDisclosure();
  const selectionToolChangeDisclosure = useDisclosure();
  const furniturePickerDisclosure = useDisclosure();
  const drawToolChangeDisclosure = useDisclosure();

  const handleIconToolClick = () => {
    if (activeTool === 'icon' || !iconToolSelectedIcon) {
      iconPickerDisclosure.open();
    }
    onToolChange('icon');
  };

  const handleIconSelection = (icon: IconValue) => {
    setIconToolSelectedIcon(icon);
    iconPickerDisclosure.close();
  };

  const handleFurnitureToolClick = () => {
    if (activeTool === 'furniture' || !furnitureToolSelectedFurniture) {
      furniturePickerDisclosure.open();
    }
    onToolChange('furniture');
  };

  const handleFurnitureSelection = (furniture: Furniture) => {
    setFurnitureToolSelectedFurniture(furniture);
    furniturePickerDisclosure.close();
  };

  const [selectionToolSelected, setSelectionToolSelected] =
    React.useState<MapEditorTool>('select');

  const [nodeToolSelected, setNodeToolSelected] =
    React.useState<MapEditorTool>('spot');

  const [drawToolSelected, setDrawToolSelected] = React.useState<DrawShape>(
    drawTool.selectedShape
  );

  const handleSelectionToolChange = (tool: MapEditorTool) => {
    if (tool !== 'draw-select' && tool !== 'select') return;
    setSelectionToolSelected(tool);
    selectionToolChangeDisclosure.close();
    onToolChange(tool);
  };

  const handleNodeToolChange = (tool: MapEditorTool) => {
    if (tool !== 'spot' && tool !== 'node-style') return;
    setNodeToolSelected(tool);
    selectionToolChangeDisclosure.close();
    onToolChange(tool);
  };

  const handleDrawToolChange = (shape: DrawShape) => {
    setDrawToolSelected(shape);
    setDrawTool(shape);
    onToolChange('draw');
    drawToolChangeDisclosure.close();
  };

  return (
    <>
      <TooltipProvider>
        <div className={classes.ToolBar}>
          {selectionToolSelected === 'select' && (
            <EditorTooltip
              message={'Selection Tool'}
              shortcut={shortcuts.global.selectionTool}
            >
              <ToolbarButton
                onClick={onToolChange.bind(null, 'select')}
                isActive={activeTool === 'select'}
              >
                <MousePointer size={20} />
              </ToolbarButton>
            </EditorTooltip>
          )}
          {selectionToolSelected === 'draw-select' && (
            <EditorTooltip
              message={'Draw Selection Tool'}
              shortcut={shortcuts.global.drawSelectTool}
            >
              <ToolbarButton
                onClick={onToolChange.bind(null, 'draw-select')}
                isActive={activeTool === 'draw-select'}
              >
                <AudioWaveform size={20} />
              </ToolbarButton>
            </EditorTooltip>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button aria-label="Open size presets">
                <ChevronUp className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center">
              <DropdownMenuItem
                onClick={handleSelectionToolChange.bind(null, 'select')}
              >
                <div className={'flex gap-2 items-center'}>
                  <MousePointer size={20} />
                  Selection Tool
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleSelectionToolChange.bind(null, 'draw-select')}
              >
                <div className={'flex gap-2 items-center'}>
                  <AudioWaveform size={20} />
                  Draw Selection
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <EditorTooltip
            message={'Pan Tool'}
            shortcut={shortcuts.global.panTool}
          >
            <ToolbarButton
              onClick={onToolChange.bind(null, 'pan')}
              isActive={activeTool === 'pan'}
            >
              <Hand size={20} />
            </ToolbarButton>
          </EditorTooltip>
          <EditorTooltip message={'Draw'}>
            <ToolbarButton
              onClick={onToolChange.bind(null, 'draw')}
              isActive={activeTool === 'draw'}
            >
              {drawToolSelected === 'rectangle' ? (
                <Square size={20} />
              ) : (
                <Circle size={20} />
              )}
            </ToolbarButton>
          </EditorTooltip>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button aria-label="Open draw shape options">
                <ChevronUp className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center">
              <DropdownMenuItem
                onClick={handleDrawToolChange.bind(null, 'rectangle')}
              >
                <div className={'flex gap-2 items-center'}>
                  <Square size={20} />
                  Rectangle
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleDrawToolChange.bind(null, 'circle')}
              >
                <div className={'flex gap-2 items-center'}>
                  <Circle size={20} />
                  Circle
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <EditorTooltip message={'Text Tool'}>
            <ToolbarButton
              onClick={onToolChange.bind(null, 'text')}
              isActive={activeTool === 'text'}
            >
              <Type size={20} />
            </ToolbarButton>
          </EditorTooltip>
          <EditorTooltip message={'Label Tool'}>
            <ToolbarButton
              onClick={onToolChange.bind(null, 'numbering')}
              isActive={activeTool === 'numbering'}
            >
              <ArrowDown01 size={20} />
            </ToolbarButton>
          </EditorTooltip>

          <Popover
            open={iconPickerDisclosure.isOpen}
            onOpenChange={(isOpen) => {
              if (!isOpen) {
                iconPickerDisclosure.close();
              }
            }}
          >
            <PopoverTrigger>
              <EditorTooltip
                message={'Icon'}
                shortcut={shortcuts.global.iconTool}
              >
                <ToolbarButton
                  onClick={handleIconToolClick}
                  isActive={activeTool === 'icon'}
                  hasChevron
                >
                  {iconToolSelectedIcon ? (
                    <IconFromSet icon={iconToolSelectedIcon} size={5} />
                  ) : (
                    <Boxes size={20} />
                  )}
                </ToolbarButton>
              </EditorTooltip>
            </PopoverTrigger>
            <PopoverContent
              arrowProps={{ className: classes.PopoverArrow }}
              className={classes.PopoverContent}
            >
              <IconPicker
                onSelect={handleIconSelection}
                initialIconSet={'tactic'}
                customSets={TacticIconsDictionary}
              />
            </PopoverContent>
          </Popover>
          <EditorTooltip message={'Furniture'}>
            <ToolbarButton
              onClick={handleFurnitureToolClick}
              isActive={activeTool === 'furniture'}
              hasChevron
            >
              <Armchair size={20} />
            </ToolbarButton>
          </EditorTooltip>
          {nodeToolSelected === 'spot' && (
            <EditorTooltip message={'Resource Creator'}>
              <ToolbarButton
                onClick={onToolChange.bind(null, 'spot')}
                isActive={activeTool === 'spot'}
              >
                <Circle size={20} />
              </ToolbarButton>
            </EditorTooltip>
          )}
          {nodeToolSelected === 'node-style' && (
            <EditorTooltip message={'Node Style'}>
              <ToolbarButton
                onClick={onToolChange.bind(null, 'node-style')}
                isActive={activeTool === 'node-style'}
              >
                <Eclipse size={20} />
              </ToolbarButton>
            </EditorTooltip>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button aria-label="Open size presets">
                <ChevronUp className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center">
              <DropdownMenuItem
                onClick={handleNodeToolChange.bind(null, 'spot')}
              >
                <div className={'flex gap-2 items-center'}>
                  <Circle size={20} />
                  Resource Creator
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleNodeToolChange.bind(null, 'node-style')}
              >
                <div className={'flex gap-2 items-center'}>
                  <Eclipse size={20} />
                  Resource Style
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TooltipProvider>
      <FurnitureSelectionModal
        open={furniturePickerDisclosure.isOpen}
        onClose={furniturePickerDisclosure.close}
        onSelect={handleFurnitureSelection}
      />
    </>
  );
};
